import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AccessoryCharge, Cart, CredentialsTokensType, PaymentCart, TokenStatusType } from '@cargos/sprintpay-models';
import { TokenService } from '@cargos/sprintpay-services';
import {
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    forkJoin,
    map,
    mergeMap,
    of,
    startWith,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { CustomerConfiguration, User } from '../models/customers';
import { PaymentMethodsSelected } from '../modules/cart/lib-cart/shared/models/payment-method.model';
import { PaymentMethodsHandlerService } from '../modules/cart/lib-cart/shared/services/payment-methods-handler.service';
import { AccessoriesService } from './accessories/accessories.service';
import { CartBillService } from './cart.service';
import { ConfigService } from './config.service';
import { CustomerService } from './customer.service';
import { DynamicsFieldsService } from './dynamic-field.service';
import { FacilityConfigurationService } from './facility-configuration.service';
import { FeatureRestrictionsService } from './features/feature-restrictions.service';
import { JwtHelperService } from './jwt-helper.service';
import { PromoCodesService } from './promo-code.service';
import { SessionService } from './session.service';
import { SummaryService } from './summary/summary.service';
@Injectable({
    providedIn: 'root',
})
export class InitialConfigurationService {
    private prevCompanyName: string | undefined = undefined;

    constructor(
        private sessionService: SessionService,
        private cartBillService: CartBillService,
        private summaryService: SummaryService,
        private accessoriesService: AccessoriesService,
        private configService: ConfigService,
        private tokenService: TokenService,
        private jwtHelperService: JwtHelperService,
        private promoCodesService: PromoCodesService,
        private customerService: CustomerService,
        private dynamicsFieldsService: DynamicsFieldsService,
        private customerConfiguration: CustomerConfiguration,
        private facilityConfigurationService: FacilityConfigurationService,
        private featureRestrictionsService: FeatureRestrictionsService,
        private paymentMethodsHandlerService: PaymentMethodsHandlerService,
        private route: ActivatedRoute,
        private router: Router
    ) {}

    setInitialConfiguration(skipLoggin = false): Observable<string | CredentialsTokensType | null> {
        return this.sessionService.hasToken$().pipe(
            mergeMap((token) => {
                if (
                    token &&
                    !this.sessionService.isAuthenticated() &&
                    this.jwtHelperService.isTokenExpired(this.tokenService.instant_token)
                ) {
                    return this.singAsAGuest();
                }

                if (token) {
                    return this.subscribeToInitialConfig();
                }

                if (!skipLoggin) {
                    return this.singAsAGuest();
                }

                return of(null);
            })
        );
    }

    singAsAGuest(): Observable<CredentialsTokensType> {
        return this.sessionService.signInAsAGuest().pipe(
            catchError((error) => {
                this.tokenService.setTokenStatus(TokenStatusType.FAILED);
                return throwError(() => error);
            })
        );
    }

    subscribeToInitialConfig(): Observable<string> {
        return forkJoin([
            this.onTokenChanges(),
            this.getCart(),
            this.getAccessories(),
            this.getTokensFromParams(),
            this.forceLogoutByParams(),
        ]).pipe(
            map(() => 'Complete'),
            catchError(() => 'Complete')
        );
    }

    getTokensFromParams(): Observable<string> {
        return this.sessionService.getTokensFromParams().pipe(
            map((tokens: CredentialsTokensType | null) => {
                if (tokens?.authorizationToken && tokens?.refreshToken) {
                    this.sessionService.postAndUpdateConfigTokens(tokens);
                }
                return 'Complete';
            })
        );
    }

    getCart(): Observable<Cart> {
        return this.sessionService.isAuthenticated$().pipe(
            switchMap((isAuthenticated) => {
                return this.getPaymentMethodSelectedAndGetCart$().pipe(
                    switchMap(() => this.cartBillService.getCartBill$().pipe(take(1)))
                );
            })
        );
    }

    getDisccount(): Observable<number | null> {
        return of(this.configService.isPromoCodeEnable()).pipe(
            switchMap((isPromoCodeEnable) => {
                if (isPromoCodeEnable) {
                    return this.promoCodesService.getAllNonReservationFee$().pipe(distinctUntilChanged());
                }

                return of(null);
            })
        );
    }

    getAccessories(): Observable<string | null> {
        const areAccesoriesEnable = this.configService.areAccesoriesEnable();

        if (!areAccesoriesEnable) {
            return of(null);
        }

        return this.getDestination().pipe(
            switchMap((location) => {
                if (location) {
                    return this.fetchAccessories(location).pipe(
                        switchMap((accessoriesResponse) => {
                            this.accessoriesService.setAccesories(accessoriesResponse);

                            return of('Complete');
                        })
                    );
                }

                return of(null);
            })
        );
    }

    getDestination(): Observable<string | null> {
        return this.summaryService.getLocation$().pipe(
            switchMap((location: string | null) => {
                if (location) {
                    return of(location);
                }

                return this.cartBillService.getCart$().pipe(
                    switchMap((cart: PaymentCart[]) => {
                        if (!cart.length) {
                            return of(null);
                        }

                        return this.cartBillService.getDestinationInCart().pipe(
                            map((destination) => {
                                this.summaryService.setAndSaveLocationInStorage(destination || null);

                                return destination || '';
                            })
                        );
                    })
                );
            })
        );
    }

    fetchAccessories(destination: string): Observable<AccessoryCharge[]> {
        return this.accessoriesService.fetchAccessories(destination);
    }

    onTokenChanges(): Observable<string> {
        return this.tokenService.getCurrentToken$().pipe(
            switchMap(() => {
                return this.updateCustomerConfig();
            })
        );
    }

    updateCustomerConfig(): Observable<string> {
        return this.getCustomerInstance().pipe(
            switchMap(() => {
                this.customerService.setLoggerUser();
                const companyId = this.customerService.getCompanyId();
                return this.dynamicsFieldsService.getDynamicFieldsPerSection(companyId).pipe(
                    take(1),
                    map(() => 'Complete')
                );
            })
        );
    }

    private getCustomerInstance(): Observable<string> {
        return of(this.configService.isEnableRequestorApprovalFlow()).pipe(
            switchMap((isRequestor) => {
                const isUserDomain = this.customerService.isUserDomain();
                if (isRequestor || isUserDomain) {
                    return of(this.customerService.getCompanyName() || '');
                }
                return of('');
            }),
            map((companyName: string) => {
                const userByCompany = this.customerConfiguration.getCustomer(companyName);
                const newUser = User.fromJson({
                    ...userByCompany,
                    config: this.facilityConfigurationService.getFacilityConfiguration(),
                });
                const { dynamicFields, dynamicSections, ...methods } = userByCompany;
                const finalUser = { ...newUser, ...methods };

                if (companyName !== this.prevCompanyName || !this.customerService.instant_customer_fields?.length) {
                    this.prevCompanyName = companyName;
                    this.customerService.setUser(finalUser);
                }
                return 'Complete';
            })
        );
    }

    getPaymentMethodSelectedAndGetCart$(): Observable<string> {
        return combineLatest([
            this.featureRestrictionsService.isPaymentMethodValid$(),
            this.promoCodesService.getNonReservationFee$().pipe(startWith(null), distinctUntilChanged()),
        ]).pipe(
            switchMap(([isPaymentMethodValid, additionalFee]) => {
                if (!isPaymentMethodValid) {
                    return this.cartBillService.getCartRequestAndSetCart().pipe(
                        take(1),
                        map(() => 'Complete')
                    );
                }

                return this.paymentMethodsHandlerService.getPaymentMethodNameByUser$().pipe(
                    switchMap((paymentMethodsSelected: PaymentMethodsSelected | null) => {
                        if (!paymentMethodsSelected) {
                            return of('error');
                        }
                        const { paymentMethodSelected, paymentMethodSelectedAboveThreshold } = paymentMethodsSelected;
                        return this.cartBillService
                            .getCartRequestAndSetCart(
                                paymentMethodSelected,
                                paymentMethodSelectedAboveThreshold,
                                additionalFee || 0
                            )
                            .pipe(
                                take(1),
                                map(() => 'Complete')
                            );
                    })
                );
            })
        );
    }

    forceLogoutByParams(): Observable<string> {
        return this.route.queryParamMap.pipe(
            map((params) => params.get('forceLogout') === '1'),
            switchMap((shouldLogout) => {
                if (!shouldLogout) {
                    return of('Complete');
                }

                return this.sessionService.logout().pipe(
                    take(1),
                    tap(() => {
                        this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: { forceLogout: null },
                            queryParamsHandling: 'merge',
                            replaceUrl: true,
                        });
                    }),
                    map(() => 'Complete')
                );
            }),
            catchError(() => of('Complete'))
        );
    }
}
